import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Download, LogOut, Loader2, RefreshCw, Trash2 } from "lucide-react";
import Logo from "../components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "cr_admin_token";

const CENTRAL_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

function formatCentral(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${CENTRAL_FMT.format(d)} CT`;
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // noindex for this private page
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "CallRetrieve · Admin";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  const loadLeads = useCallback(async (tok) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setLeads(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Session expired. Please sign in again.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Could not load leads. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadLeads(token);
  }, [token, loadLeads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const tok = res.data.token;
      localStorage.setItem(TOKEN_KEY, tok);
      setToken(tok);
      setPassword("");
    } catch (err) {
      setError(err?.response?.status === 401 ? "Incorrect password." : "Login failed. Try again.");
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
  };

  const deleteLead = async (lead) => {
    const label = lead.name ? `"${lead.name}"` : "this lead";
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/leads/${lead.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Session expired. Please sign in again.");
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError("Could not delete lead. Try again.");
      }
    }
  };

  const downloadCsv = () => {
    const header = ["Name", "Email", "Business type", "Submitted (US Central)"];
    const rows = leads.map((l) => [
      l.name || "",
      l.email || "",
      l.business_type || "",
      formatCentral(l.created_at),
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `callretrieve-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div className="min-h-screen bg-cream grain flex flex-col">
        <header className="px-6 md:px-10 py-6">
          <Logo />
        </header>
        <div className="flex-1 flex items-center justify-center px-6">
          <form
            onSubmit={handleLogin}
            className="w-full max-w-sm bg-white rounded-3xl border border-navy/10 shadow-xl p-8 md:p-10"
          >
            <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center">
              <Lock className="w-5 h-5 text-lime" />
            </div>
            <h1 className="mt-5 font-grotesk text-2xl font-bold text-navy">Admin access</h1>
            <p className="mt-1 text-navy/55 text-sm">Enter the admin password to view leads.</p>

            <label className="mt-7 block font-mono text-[11px] uppercase tracking-[0.18em] text-navy/50">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="mt-2 w-full h-12 px-4 bg-cream rounded-xl border border-navy/10 text-navy focus:outline-none focus:ring-2 focus:ring-teal/40"
            />

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={loading || !password}
              className="mt-6 w-full h-12 inline-flex items-center justify-center gap-2 bg-navy text-cream font-grotesk font-semibold rounded-full hover:bg-navy-800 transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---- Dashboard ----
  return (
    <div className="min-h-screen bg-cream grain">
      <header className="bg-navy">
        <div className="max-w-[1180px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo dark size={34} />
            <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.2em] text-lime">
              Leads Admin
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-cream/80 hover:text-cream text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-[1180px] mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-grotesk text-3xl font-bold text-navy">Contact leads</h1>
            <p className="text-navy/55 mt-1">
              {leads.length} submission{leads.length === 1 ? "" : "s"} · newest first · times in US Central
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadLeads(token)}
              className="inline-flex items-center gap-2 border border-navy/15 text-navy px-4 py-2.5 rounded-full text-sm hover:bg-navy/5 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={downloadCsv}
              disabled={leads.length === 0}
              className="inline-flex items-center gap-2 bg-lime text-navy font-grotesk font-semibold px-5 py-2.5 rounded-full hover:bg-lime-bright transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" /> Download CSV
            </button>
          </div>
        </div>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <div className="mt-6 bg-white rounded-2xl border border-navy/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-navy/[0.03] border-b border-navy/10">
                  <th className="px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.14em] text-navy/50">Name</th>
                  <th className="px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.14em] text-navy/50">Email</th>
                  <th className="px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.14em] text-navy/50">Business type</th>
                  <th className="px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.14em] text-navy/50">Submitted</th>
                  <th className="px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.14em] text-navy/50 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && leads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-navy/50">
                      <Loader2 className="w-5 h-5 animate-spin inline" />
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center text-navy/50">
                      No leads yet.
                    </td>
                  </tr>
                ) : (
                  leads.map((l) => (
                    <tr key={l.id} className="border-b border-navy/8 last:border-0 hover:bg-navy/[0.02]">
                      <td className="px-5 py-4 text-navy font-medium">{l.name}</td>
                      <td className="px-5 py-4 text-navy/75">
                        <a href={`mailto:${l.email}`} className="hover:text-teal transition-colors">{l.email}</a>
                      </td>
                      <td className="px-5 py-4">
                        {l.business_type ? (
                          <span className="inline-block bg-navy/5 text-navy/80 text-[13px] px-2.5 py-1 rounded-full">
                            {l.business_type}
                          </span>
                        ) : (
                          <span className="text-navy/35">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 font-mono text-[13px] text-navy/60">{formatCentral(l.created_at)}</td>
                      <td className="px-5 py-4 text-right">
                        <button
                          onClick={() => deleteLead(l)}
                          title="Delete lead"
                          className="inline-flex items-center gap-1.5 text-navy/50 hover:text-red-600 border border-navy/10 hover:border-red-300 hover:bg-red-50 px-3 py-1.5 rounded-full text-[13px] transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
